// =============================================================================
// FILE:
// frontend/src/modules/pms/material_logistics/pages/ReconciliationWorkbenchPage.js
// =============================================================================

import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  useParams
} from "react-router-dom";

import {

  getReceiptDetail,
  saveReconciliation,
  reconcileReceipt

} from "../services/materialReceiptService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function ReconciliationWorkbenchPage() {

  // ===========================================================================
  // PARAMS
  // ===========================================================================
  const {
    receiptId
  } = useParams();


  // ===========================================================================
  // STATE
  // ===========================================================================
  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [detail, setDetail] =
    useState(null);

  const [remarks, setRemarks] =
    useState("");

  const [lines, setLines] =
    useState([]);

  const [actionLoading, setActionLoading] =
    useState(false);


  // ===========================================================================
  // LOAD DETAIL
  // ===========================================================================
  const loadDetail = async () => {

    try {

      setLoading(true);

      setError("");

      const response =
        await getReceiptDetail(
          receiptId
        );

      if (response?.success) {

        const data =
          response.data;

        setDetail(data);

        setRemarks(
          data?.header?.remarks || ""
        );

        setLines(

          (data?.lines || []).map((line) => {

            const receivedQty =
              Number(
                line.received_qty || 0
              );

            const shortageQty =
              Number(
                line.shortage_qty || 0
              );

            const damagedQty =
              Number(
                line.damaged_qty || 0
              );

            const rejectedQty =
              Number(
                line.rejected_qty || 0
              );

            const acceptedQty =

              receivedQty
              -
              shortageQty
              -
              damagedQty
              -
              rejectedQty;

            return {

              ...line,

              shortage_qty:
                shortageQty,

              damaged_qty:
                damagedQty,

              rejected_qty:
                rejectedQty,

              accepted_qty:
                acceptedQty
            };
          })
        );

      } else {

        setError(
          "Failed to load reconciliation detail"
        );
      }

    } catch (err) {

      console.error(err);

      setError(
        "Failed to load reconciliation detail"
      );

    } finally {

      setLoading(false);
    }
  };


  // ===========================================================================
  // UPDATE LINE
  // ===========================================================================
  const updateLine = (
    index,
    field,
    value
  ) => {

    const updated =
      [...lines];

    updated[index][field] =
      Number(value);

    const receivedQty =
      Number(
        updated[index].received_qty || 0
      );

    const shortageQty =
      Number(
        updated[index].shortage_qty || 0
      );

    const damagedQty =
      Number(
        updated[index].damaged_qty || 0
      );

    const rejectedQty =
      Number(
        updated[index].rejected_qty || 0
      );

    updated[index].accepted_qty =

      receivedQty
      -
      shortageQty
      -
      damagedQty
      -
      rejectedQty;

    setLines(updated);
  };


  // ===========================================================================
  // SAVE RECONCILIATION
  // ===========================================================================
  const handleSaveReconciliation =
    async () => {

      try {

        setActionLoading(true);

        await saveReconciliation(

          receiptId,

          {

            remarks,

            lines: lines.map((line) => ({

              id: line.id,

              shortage_qty:
                Number(
                  line.shortage_qty || 0
                ),

              damaged_qty:
                Number(
                  line.damaged_qty || 0
                ),

              rejected_qty:
                Number(
                  line.rejected_qty || 0
                )
            }))
          }
        );

        alert(
          "Reconciliation saved successfully"
        );

        loadDetail();

      } catch (err) {

        console.error(err);

        alert(

          err?.response?.data?.message ||

          "Failed to save reconciliation"
        );

      } finally {

        setActionLoading(false);
      }
    };


  // ===========================================================================
  // APPROVE RECONCILIATION
  // ===========================================================================
  const handleApproveReconciliation =
    async () => {

      try {

        const confirmed =
          window.confirm(

            "Approve reconciliation and post inventory?"
          );

        if (!confirmed) {

          return;
        }

        setActionLoading(true);

        await reconcileReceipt(
          receiptId
        );

        alert(
          "Reconciliation approved successfully"
        );

        loadDetail();

      } catch (err) {

        console.error(err);

        alert(

          err?.response?.data?.message ||

          "Failed to approve reconciliation"
        );

      } finally {

        setActionLoading(false);
      }
    };


  // ===========================================================================
  // INITIAL LOAD
  // ===========================================================================
  useEffect(() => {

    loadDetail();

  }, [receiptId]);


  // ===========================================================================
  // HEADER
  // ===========================================================================
  const header =
    detail?.header || {};


  // ===========================================================================
  // SUMMARY
  // ===========================================================================
  const totals =
    useMemo(() => {

      return lines.reduce(

        (acc, line) => {

          acc.received_qty +=
            Number(
              line.received_qty || 0
            );

          acc.shortage_qty +=
            Number(
              line.shortage_qty || 0
            );

          acc.damaged_qty +=
            Number(
              line.damaged_qty || 0
            );

          acc.rejected_qty +=
            Number(
              line.rejected_qty || 0
            );

          acc.accepted_qty +=
            Number(
              line.accepted_qty || 0
            );

          return acc;

        },

        {

          received_qty: 0,

          shortage_qty: 0,

          damaged_qty: 0,

          rejected_qty: 0,

          accepted_qty: 0
        }
      );

    }, [lines]);


  // ===========================================================================
  // LOADING
  // ===========================================================================
  if (loading) {

    return (

      <div style={styles.loader}>

        Loading reconciliation detail...

      </div>
    );
  }


  // ===========================================================================
  // ERROR
  // ===========================================================================
  if (error) {

    return (

      <div style={styles.errorBox}>

        {error}

      </div>
    );
  }


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.page}>


      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <div style={styles.headerCard}>


        <div>

          <div style={styles.title}>

            {

              header.receipt_no ||

              "Reconciliation Workbench"
            }

          </div>

          <div style={styles.subTitle}>

            Final reconciliation and inventory acceptance

          </div>

        </div>


        <div style={styles.headerRight}>


          <span style={
            getStatusStyle(
              header.receipt_status
            )
          }>

            {

              header.receipt_status ||

              "SUBMITTED"
            }

          </span>


          {

            header.receipt_status !==
            "RECONCILED" && (

              <>

                <button
                  style={styles.saveBtn}
                  onClick={
                    handleSaveReconciliation
                  }
                  disabled={actionLoading}
                >

                  Save Reconciliation

                </button>


                <button
                  style={styles.approveBtn}
                  onClick={
                    handleApproveReconciliation
                  }
                  disabled={actionLoading}
                >

                  Approve Reconciliation

                </button>

              </>
            )
          }

        </div>

      </div>


      {/* ================================================================ */}
      {/* SUMMARY */}
      {/* ================================================================ */}
      <div style={styles.sectionCard}>


        <div style={styles.sectionTitle}>
          Reconciliation Summary
        </div>


        <div style={styles.summaryGrid}>


          <SummaryItem
            label="Dispatch No"
            value={
              header.dispatch_no
            }
          />

          <SummaryItem
            label="Receipt Date"
            value={
              header.receipt_date
            }
          />

          <SummaryItem
            label="Received Qty"
            value={
              totals.received_qty
            }
          />

          <SummaryItem
            label="Accepted Qty"
            value={
              totals.accepted_qty
            }
          />

          <SummaryItem
            label="Rejected Qty"
            value={
              totals.rejected_qty
            }
          />

          <SummaryItem
            label="Damaged Qty"
            value={
              totals.damaged_qty
            }
          />

        </div>

      </div>


      {/* ================================================================ */}
      {/* WORKFLOW */}
      {/* ================================================================ */}
      <div style={styles.sectionCard}>


        <div style={styles.sectionTitle}>
          Workflow Status
        </div>


        <div style={styles.workflowBar}>


          {

            workflowSteps.map((step) => {

              const active =
                step ===
                header.receipt_status;

              return (

                <div
                  key={step}
                  style={
                    active
                      ? styles.activeStep
                      : styles.step
                  }
                >

                  {step}

                </div>
              );
            })
          }

        </div>

      </div>


      {/* ================================================================ */}
      {/* RECONCILIATION GRID */}
      {/* ================================================================ */}
      <div style={styles.sectionCard}>


        <div style={styles.sectionTitle}>

          Reconciliation Verification
          {" "}
          ({lines.length})

        </div>


        <div style={styles.lineTable}>


          {/* ============================================================ */}
          {/* HEADER */}
          {/* ============================================================ */}
          <div style={styles.tableHeader}>


            <div style={styles.colMaterial}>
              Material
            </div>

            <div style={styles.colQty}>
              Received
            </div>

            <div style={styles.colQty}>
              Shortage
            </div>

            <div style={styles.colQty}>
              Damaged
            </div>

            <div style={styles.colQty}>
              Rejected
            </div>

            <div style={styles.colQty}>
              Accepted
            </div>

          </div>


          {/* ============================================================ */}
          {/* ROWS */}
          {/* ============================================================ */}
          {

            lines.map((line, index) => (

              <div
                key={line.id}
                style={styles.lineRow}
              >


                <div style={styles.colMaterial}>


                  <div style={styles.materialName}>

                    {

                      line.material_name ||

                      line.material_description
                    }

                  </div>


                  <div style={styles.materialSub}>

                    Code:
                    {" "}

                    {

                      line.material_code || "-"
                    }

                  </div>

                </div>


                <div style={styles.colQty}>

                  {line.received_qty}

                </div>


                <div style={styles.colQty}>


                  <input

                    type="number"

                    value={
                      line.shortage_qty
                    }

                    onChange={(e) =>

                      updateLine(

                        index,

                        "shortage_qty",

                        e.target.value
                      )
                    }

                    style={styles.input}

                    disabled={
                      header.receipt_status ===
                      "RECONCILED"
                    }
                  />

                </div>


                <div style={styles.colQty}>


                  <input

                    type="number"

                    value={
                      line.damaged_qty
                    }

                    onChange={(e) =>

                      updateLine(

                        index,

                        "damaged_qty",

                        e.target.value
                      )
                    }

                    style={styles.input}

                    disabled={
                      header.receipt_status ===
                      "RECONCILED"
                    }
                  />

                </div>


                <div style={styles.colQty}>


                  <input

                    type="number"

                    value={
                      line.rejected_qty
                    }

                    onChange={(e) =>

                      updateLine(

                        index,

                        "rejected_qty",

                        e.target.value
                      )
                    }

                    style={styles.input}

                    disabled={
                      header.receipt_status ===
                      "RECONCILED"
                    }
                  />

                </div>


                <div style={styles.colQty}>


                  <div style={styles.acceptedBox}>

                    {line.accepted_qty}

                  </div>

                </div>

              </div>
            ))
          }

        </div>

      </div>


      {/* ================================================================ */}
      {/* REMARKS */}
      {/* ================================================================ */}
      <div style={styles.sectionCard}>


        <div style={styles.sectionTitle}>
          Reconciliation Remarks
        </div>


        <textarea

          value={remarks}

          onChange={(e) =>
            setRemarks(e.target.value)
          }

          style={styles.textArea}

          disabled={
            header.receipt_status ===
            "RECONCILED"
          }
        />

      </div>

    </div>
  );
}


// =============================================================================
// SUMMARY ITEM
// =============================================================================

function SummaryItem({

  label,

  value

}) {

  return (

    <div style={styles.summaryItem}>

      <div style={styles.summaryLabel}>
        {label}
      </div>

      <div style={styles.summaryValue}>
        {value || 0}
      </div>

    </div>
  );
}


// =============================================================================
// WORKFLOW
// =============================================================================

const workflowSteps = [

  "RECEIVED",

  "BOX_VERIFIED",

  "SUBMITTED",

  "RECONCILED"
];


// =============================================================================
// STATUS STYLE
// =============================================================================

function getStatusStyle(status) {

  switch (status) {

    case "RECONCILED":

      return styles.statusGreen;

    case "SUBMITTED":

      return styles.statusOrange;

    default:

      return styles.statusGray;
  }
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  page: {

    padding: 20,

    background: "#f5f7fb",

    minHeight: "100vh"
  },

  loader: {

    padding: 40,

    textAlign: "center"
  },

  errorBox: {

    margin: 20,

    padding: 20,

    background: "#fee2e2",

    color: "#b91c1c",

    borderRadius: 12
  },

  headerCard: {

    background:
      "linear-gradient(90deg, #059669, #10b981)",

    borderRadius: 14,

    padding: 20,

    marginBottom: 20,

    color: "#ffffff",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  headerRight: {

    display: "flex",

    alignItems: "center",

    gap: 12
  },

  saveBtn: {

    background: "#111827",

    color: "#fff",

    border: "none",

    borderRadius: 8,

    padding: "10px 16px",

    cursor: "pointer",

    fontWeight: 600
  },

  approveBtn: {

    background: "#16a34a",

    color: "#fff",

    border: "none",

    borderRadius: 8,

    padding: "10px 16px",

    cursor: "pointer",

    fontWeight: 600
  },

  title: {

    fontSize: 24,

    fontWeight: 700
  },

  subTitle: {

    marginTop: 6,

    fontSize: 13,

    opacity: 0.9
  },

  sectionCard: {

    background: "#ffffff",

    borderRadius: 12,

    padding: 20,

    marginBottom: 20,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  sectionTitle: {

    fontSize: 16,

    fontWeight: 700,

    color: "#111827",

    marginBottom: 16
  },

  summaryGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(220px, 1fr))",

    gap: 16
  },

  summaryItem: {

    background: "#f9fafb",

    borderRadius: 10,

    padding: 14
  },

  summaryLabel: {

    fontSize: 12,

    color: "#6b7280",

    marginBottom: 6
  },

  summaryValue: {

    fontSize: 14,

    fontWeight: 600,

    color: "#111827"
  },

  workflowBar: {

    display: "flex",

    gap: 12,

    flexWrap: "wrap"
  },

  activeStep: {

    background: "#059669",

    color: "#ffffff",

    padding: "10px 16px",

    borderRadius: 30,

    fontSize: 12,

    fontWeight: 700
  },

  step: {

    background: "#e5e7eb",

    color: "#374151",

    padding: "10px 16px",

    borderRadius: 30,

    fontSize: 12,

    fontWeight: 700
  },

  lineTable: {

    display: "flex",

    flexDirection: "column",

    gap: 10
  },

  tableHeader: {

    display: "grid",

    gridTemplateColumns:
      "2.5fr 1fr 1fr 1fr 1fr 1fr",

    gap: 16,

    padding: "14px 16px",

    background: "#f9fafb",

    border:
      "1px solid #e5e7eb",

    borderRadius: 10,

    fontSize: 13,

    fontWeight: 700,

    color: "#374151"
  },

  lineRow: {

    display: "grid",

    gridTemplateColumns:
      "2.5fr 1fr 1fr 1fr 1fr 1fr",

    gap: 16,

    padding: "14px 16px",

    border:
      "1px solid #e5e7eb",

    borderRadius: 10,

    alignItems: "center",

    background: "#ffffff"
  },

  colMaterial: {

    display: "flex",

    flexDirection: "column"
  },

  colQty: {

    display: "flex",

    alignItems: "center",

    justifyContent: "center"
  },

  materialName: {

    fontWeight: 600,

    color: "#111827"
  },

  materialSub: {

    marginTop: 4,

    fontSize: 12,

    color: "#6b7280"
  },

  input: {

    width: "100%",

    border:
      "1px solid #d1d5db",

    borderRadius: 8,

    padding: "8px 10px"
  },

  acceptedBox: {

    background: "#dcfce7",

    color: "#15803d",

    padding: "8px 12px",

    borderRadius: 8,

    fontWeight: 700,

    minWidth: 60,

    textAlign: "center"
  },

  textArea: {

    width: "100%",

    minHeight: 120,

    border:
      "1px solid #d1d5db",

    borderRadius: 10,

    padding: 14
  },

  statusGray: {

    background: "#f3f4f6",

    color: "#374151",

    padding: "8px 14px",

    borderRadius: 20,

    fontSize: 12,

    fontWeight: 700
  },

  statusOrange: {

    background: "#ffedd5",

    color: "#c2410c",

    padding: "8px 14px",

    borderRadius: 20,

    fontSize: 12,

    fontWeight: 700
  },

  statusGreen: {

    background: "#dcfce7",

    color: "#15803d",

    padding: "8px 14px",

    borderRadius: 20,

    fontSize: 12,

    fontWeight: 700
  }
};